import ClientWorkspace from './components/ClientWorkspace';
import React, { useState, useEffect, Suspense, lazy } from 'react';
import './App.css';
import Sidebar from './components/Sidebar';
import Header from './components/Header';
import Login, {SsoSessionExchange} from './components/Login';
import PublicLanding from './components/PublicLanding';
import OnboardingSurvey from './components/OnboardingSurvey';

import LearnerPortal from './components/LearnerPortal';
import CourseCatalogue from './components/CourseCatalogue';
import LessonPlayer from './components/LessonPlayer';
import Certificate from './components/Certificate';
import Profile from './components/Profile';
import Library from './components/Library';
import GuidedFinder from './components/GuidedFinder';
import FeedbackButton from './components/FeedbackButton';
import AudienceScreen from './components/presentation/AudienceScreen';
import LoadingSpinner from './components/LoadingSpinner';

// Admin, studio and editor screens load only when someone opens them, so the
// first load for a learner stays small on a slow connection.
const AdminDashboard = lazy(() => import('./components/AdminDashboard'));
const ComplianceView = lazy(() => import('./components/ComplianceView'));
const AuditLogView = lazy(() => import('./components/AuditLogView'));
const OrganisationView = lazy(() => import('./components/OrganisationView'));
const OrganisationsAdmin = lazy(() => import('./components/OrganisationsAdmin'));
const FeedbackInbox = lazy(() => import('./components/FeedbackInbox'));
const StudioShell = lazy(() => import('./components/presentation/StudioShell'));
const UsersPanel = lazy(() => import('./components/UsersPanel'));
const CourseEditView = lazy(() => import('./components/CourseEditor').then(m => ({ default: m.CourseEditView })));
import ThemeToggle from './components/ThemeToggle';
import Footer from './components/Footer';
import { getSessionToken, setSessionToken, SIGNED_OUT_EVENT } from './utils/session';
import { GraduationCap, BookOpen, Award, User, LayoutGrid, MessageSquare, Presentation, Library as LibraryIcon, Users as UsersIcon, PencilRuler, ClipboardCheck, History, Building2, Search } from 'lucide-react';

const VIEW_MODE_KEY = 'okiru_view_mode';
const VIEW_TITLES = {
  clients: 'Clients', landing: 'Okiru Learn', 'sign-in': 'Sign in',
  portal: 'My learning', finder: 'Find your course', catalogue: 'Course catalogue', library: 'Library', lesson: 'Lesson',
  certificates: 'Certificates', profile: 'My profile', feedback: 'Feedback inbox', admin: 'Admin dashboard',
  users: 'Users', 'course-edit': 'Edit course', 'presentation-studio': 'Presentation studio', compliance: 'Compliance', audit: 'Activity log', organisation: 'My organisation', organisations: 'Organisations',
};

// The address bar follows the screen (#/lesson/<course>/<module>), so refresh
// keeps your place, Back works and a course can be linked to.
const toHash = (view, courseId, moduleId, editId) => {
  const parts = [view];
  if (view === 'lesson' && courseId) { parts.push(courseId); if (moduleId) parts.push(moduleId); }
  if (view === 'course-edit' && editId) parts.push(editId);
  return `#/${parts.map(encodeURIComponent).join('/')}`;
};
const fromHash = () => {
  const match = window.location.hash.match(/^#\/([^?]*)$/);
  if (!match) return null;
  const [view, a, b] = match[1].split('/').map(decodeURIComponent);
  if (!VIEW_TITLES[view]) return null;
  if (view === 'lesson') return { view, courseId: a || null, moduleId: b || null };
  if (view === 'course-edit') return { view, editId: a || null };
  return { view };
};

// A live class's projector window is opened via window.open('/?audience=...')
// from PresenterView — it's a fresh page load with no login state, so it's
// handled here before the normal auth gate, the same way ?reset=/?sso= are.
function useAudienceScreenParams() {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('audience');
  const presentationId = params.get('p');
  return sessionId ? { sessionId, presentationId } : null;
}

export default function App() {
  const [user, setUser]                     = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [view, setView]                     = useState(() => fromHash()?.view === 'sign-in' ? 'sign-in' : 'landing');
  const [selectedCourse, setSelectedCourse] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [editCourseId, setEditCourseId] = useState(null);
  const [usersFilter, setUsersFilter]   = useState('all');
  const [catalogueSearch, setCatalogueSearch] = useState({ query: '', nonce: 0 });
  const [editableCourses, setEditableCourses] = useState([]);
  // How many classroom courses a non-admin (facilitator or named presenter)
  // can present; the Presentation Studio appears for them when it is above 0.
  const [presentCourseCount, setPresentCourseCount] = useState(0);
  // The page bootstrap in public/index.html has already set data-theme (light
  // unless the person chose dark with the toggle).
  const [theme, setTheme]                   = useState(() => document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light');
  // Admins who are also candidates (Chengetai, Tshiamo, Zoleka) can switch
  // between the full admin surface and the exact view a student sees,
  // without their underlying role ever changing.
  const [viewMode, setViewMode]             = useState(() => localStorage.getItem(VIEW_MODE_KEY) || 'admin');

  // Set when the server ends a session (token expired, or access window closed).
  const [signInNotice, setSignInNotice] = useState('');
  const [ssoToken] = useState(() => new URLSearchParams(window.location.search).get('sso'));
  // True while a saved session is being checked, so the sign-in form does not flash.
  const [restoring, setRestoring] = useState(() => !!getSessionToken());

  const [enrollments,   setEnrollments]   = useState(() => new Set());
  const [progress,      setProgress]      = useState({});
  const [quizScores,    setQuizScores]    = useState({});
  const [certificates,  setCertificates]  = useState({});
  const [assignments,   setAssignments]   = useState({});
  const [pendingSaves,  setPendingSaves]  = useState(0);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', theme === 'dark' ? '#08080A' : '#F1F2F0');
  }, [theme]);

  // Pick up a saved session after a reload, and sign out when the server says
  // the session or the learner's access has ended.
  useEffect(() => {
    if (!getSessionToken()) { setRestoring(false); return undefined; }
    let cancelled = false;
    fetch('/api/auth/me')
      .then(r => r.json())
      .then(j => { if (!cancelled && j.success) handleLogin(j.data); })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRestoring(false); });
    const onSignedOut = (e) => {
      setSignInNotice(e.detail?.message || '');
      handleLogout(true);
    };
    window.addEventListener(SIGNED_OUT_EVENT, onSignedOut);
    return () => { cancelled = true; window.removeEventListener(SIGNED_OUT_EVENT, onSignedOut); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!user || user.role === 'admin') { setPresentCourseCount(0); return; }
    fetch(`/api/present/courses?userId=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(j => setPresentCourseCount(j.success ? j.data.filter(c => c.decks.some(d => d.canPresent)).length : 0))
      .catch(() => setPresentCourseCount(0));
  }, [user]);

  // Courses the signed-in admin or facilitator may edit, for the sidebar.
  const canEditCourses = user && (user.role === 'admin' || user.role === 'facilitator');
  const loadEditableCourses = React.useCallback(() => {
    if (!canEditCourses) { setEditableCourses([]); return; }
    fetch(`/api/editor/courses?userId=${encodeURIComponent(user.id)}`)
      .then(r => r.json())
      .then(j => setEditableCourses(j.success ? j.data : []))
      .catch(() => setEditableCourses([]));
  }, [canEditCourses, user]);
  useEffect(() => { loadEditableCourses(); }, [loadEditableCourses]);

  // Only an explicit choice is remembered, so first visits are always light.
  const toggleTheme = () => setTheme(t => {
    const next = t === 'dark' ? 'light' : 'dark';
    try { localStorage.setItem('okiru_theme_choice', next); } catch { /* storage unavailable */ }
    return next;
  });

  // Each screen gets its own tab title, and focus moves to the content when the
  // screen changes so keyboard and screen reader users start in the right place.
  const mainRef = React.useRef(null);
  useEffect(() => {
    const label = VIEW_TITLES[view] || 'Okiru Learn';
    document.title = user ? `${label} | Okiru Learn` : 'Sign in | Okiru Learn';
    if (user && mainRef.current) mainRef.current.focus({ preventScroll: true });
  }, [view, user]);

  const toggleViewMode = () => {
    setViewMode(m => {
      const next = m === 'admin' ? 'student' : 'admin';
      localStorage.setItem(VIEW_MODE_KEY, next);
      navigateTo(next === 'admin' ? 'admin' : 'portal');
      return next;
    });
  };

  // Retry any progress that could not be saved: on sign-in, when the connection
  // returns, and every minute.
  useEffect(() => {
    if (!user) return undefined;
    const run = () => { flushPending(); };
    run();
    window.addEventListener('online', run);
    const id = setInterval(run, 60 * 1000);
    return () => { window.removeEventListener('online', run); clearInterval(id); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Back and Forward follow the address bar.
  useEffect(() => {
    if (!user) return undefined;
    const onPop = () => {
      const link = fromHash();
      if (!link) return;
      setView(link.view);
      if (link.courseId) setSelectedCourse(link.courseId);
      if (link.moduleId) setSelectedModule(link.moduleId);
      if (link.editId) setEditCourseId(link.editId);
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [user]);

  // Heartbeat — reports every 30s while a learner is active (including an
  // admin who's currently switched into the student view)
  useEffect(() => {
    if (!user || (user.role === 'admin' && viewMode === 'admin')) return;
    const INTERVAL = 30;
    const id = setInterval(() => {
      fetch('/api/learner/heartbeat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, seconds: INTERVAL }),
      }).catch(() => {});
    }, INTERVAL * 1000);
    return () => clearInterval(id);
  }, [user, viewMode]);

  // The audience/projector window — a plain window.open() target with no
  // login of its own, so it's resolved before anything else needs `user`.
  const audienceParams = useAudienceScreenParams();
  if (audienceParams) {
    return <AudienceScreen sessionId={audienceParams.sessionId} presentationId={audienceParams.presentationId} />;
  }

  const navigateTo = (v, courseId = null, moduleId = null, editId = null) => {
    setView(v);
    if (courseId !== null) setSelectedCourse(courseId);
    if (moduleId !== null) setSelectedModule(moduleId);
    if (editId !== null) setEditCourseId(editId);
    const next = toHash(v, courseId !== null ? courseId : selectedCourse, moduleId !== null ? moduleId : selectedModule, editId !== null ? editId : editCourseId);
    if (window.location.hash !== next) window.history.pushState(null, '', next);
  };

  const enroll = async (courseId) => {
    setEnrollments(prev => new Set([...prev, courseId]));
    if (user) {
      fetch('/api/learner/enroll', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: user.id, courseId }),
      }).catch(() => {});
    }
  };

  // Lesson completions that could not be saved are kept on this device and
  // retried, so a dropped connection does not lose progress. The learner is told
  // while any are waiting.
  const pendingKey = user ? `okiru_pending_lessons:${user.id}` : null;
  const readPending = () => { try { return JSON.parse(localStorage.getItem(pendingKey) || '[]'); } catch { return []; } };
  const writePending = (ids) => {
    try { localStorage.setItem(pendingKey, JSON.stringify(ids)); } catch { /* storage unavailable */ }
    setPendingSaves(ids.length);
  };
  const postLesson = async (lessonId) => {
    const res = await fetch('/api/learner/lesson-complete', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userId: user.id, lessonId }),
    });
    if (!res.ok) throw new Error('not saved');
  };
  const flushPending = async () => {
    if (!user) return;
    const waiting = readPending();
    if (!waiting.length) { setPendingSaves(0); return; }
    const stillWaiting = [];
    for (const id of waiting) {
      try { await postLesson(id); } catch { stillWaiting.push(id); }
    }
    writePending(stillWaiting);
  };

  const markLessonComplete = async (lessonId) => {
    setProgress(prev => ({ ...prev, [lessonId]: true }));
    if (!user) return;
    try {
      await postLesson(lessonId);
    } catch {
      const waiting = readPending();
      if (!waiting.includes(lessonId)) writePending([...waiting, lessonId]);
    }
  };

  // The server grades the answers. The browser never sends a score, only the
  // answers, and shows the result it gets back. Throws if the attempt could not
  // be saved, so the quiz can keep the learner's answers and let them retry.
  const submitQuiz = async (courseId, quizId, answers) => {
    const res  = await fetch('/api/learner/quiz-attempt', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ courseId, quizId, answers }),
    });
    const json = await res.json().catch(() => ({}));
    if (!res.ok || !json.success) throw new Error(json.error || 'Your answers could not be submitted.');
    // A fail never replaces an earlier pass in the progress screens.
    setQuizScores(prev => (prev[quizId]?.passed && !json.result.passed ? prev : { ...prev, [quizId]: json.result }));
    return json.result;
  };

  const handleLogin = async (userData) => {
    setUser(userData);
    setShowOnboarding(userData.role !== 'admin' && !userData.onboardingCompletedAt);
    const link = fromHash();
    if (link && !['landing', 'sign-in'].includes(link.view)) {
      setView(link.view);
      if (link.courseId) setSelectedCourse(link.courseId);
      if (link.moduleId) setSelectedModule(link.moduleId);
      if (link.editId) setEditCourseId(link.editId);
    } else {
      setView('clients');
    }
    // Fetch progress for everyone, including admins: an admin who switches to
    // the student view should see their own real enrollments and history.
    try {
      const res  = await fetch(`/api/learner/progress/${userData.id}`);
      const json = await res.json();
      if (json.success) {
        setEnrollments(new Set(json.data.enrollments || []));
        setProgress(json.data.progress || {});
        setQuizScores(json.data.quizScores || {});
        setCertificates(json.data.certificates || {});
        setAssignments(json.data.assignments || {});
      }
    } catch {}
  };

  // `local` is true when the server already ended the session, so there is no
  // need to call it again.
  const handleLogout = (local = false) => {
    if (local !== true) fetch('/api/auth/logout', { method: 'POST' }).catch(() => {});
    setSessionToken('');
    setUser(null);
    setView('landing');
    setEnrollments(new Set());
    setProgress({});
    setQuizScores({});
    setCertificates({});
    setAssignments({});
  };

  // Handle SSO inbound from Okiru.app: ?sso=SESSION_TOKEN
  if (ssoToken && !user) return <SsoSessionExchange token={ssoToken} onLogin={handleLogin}/>;
  if (new URLSearchParams(window.location.search).has('reset')) {
    window.location.replace('https://okiru.app/toolkit-auth?toolkit=learn&method=recovery');
    return null;
  }

  if (!user && restoring) return null;
  if (!user) {
    if (view === 'sign-in') {
      return (
        <>
          <button type="button" className="signed-out-back" onClick={() => navigateTo('landing')}>Back to Okiru Learn</button>
          <Login onLogin={(u) => { setSignInNotice(''); handleLogin(u); }} notice={signInNotice} />
          <ThemeToggle theme={theme} onToggle={toggleTheme} />
        </>
      );
    }
    return (
      <>
        <PublicLanding onSignIn={() => navigateTo('sign-in')} />
        <ThemeToggle theme={theme} onToggle={toggleTheme} />
      </>
    );
  }

  if (showOnboarding) {
    return (
      <OnboardingSurvey
        user={user}
        onDone={(updated) => { setUser(u => ({ ...u, ...updated })); setShowOnboarding(false); }}
        onSkip={() => setShowOnboarding(false)}
      />
    );
  }

  // True admins can flip to the exact view a candidate sees without losing
  // their role; everyone else is always in their one and only view.
  const isAdminView = user.role === 'admin' && viewMode === 'admin';

  // Learners (and admins currently viewing as a student) must never reach admin-only views
  const ADMIN_VIEWS = ['admin', 'users', 'compliance', 'audit', 'organisations'];
  if (!isAdminView && ADMIN_VIEWS.includes(view)) {
    setView('portal');
    return null;
  }
  const isFacilitator = user.role === 'facilitator';
  if (view === 'course-edit' && !isAdminView && !isFacilitator) {
    setView('portal');
    return null;
  }
  // The Presentation Studio is for admins (in the admin view), facilitators and
  // named presenters. An admin in the student view never sees it.
  const canUseStudio = isAdminView || (user.role !== 'admin' && presentCourseCount > 0);
  if ((view === 'presentation-studio' && !canUseStudio) || view === 'presenter') {
    setView('portal');
    return null;
  }

  // Asks the server to issue the certificate. It does so only if the server has
  // itself graded a passing final assessment for this learner and course.
  const issueCertificate = async (courseId) => {
    if (!user) return;
    try {
      const res  = await fetch('/api/learner/certificate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ courseId }),
      });
      const json = await res.json();
      if (json.success) {
        setCertificates(prev => ({ ...prev, [courseId]: { score: json.score, issuedAt: json.issuedAt, certId: json.certId, certNumber: json.certNumber } }));
      }
    } catch { /* the learner can open Certificates later; the pass is stored */ }
  };

  const ctx = { user, isAdmin: isAdminView, enrollments, progress, quizScores, certificates, assignments, enroll, markLessonComplete, submitQuiz, issueCertificate, navigateTo };

  const selectCourseSearchResult = course => {
    setCatalogueSearch({ query: course.title, nonce: Date.now() });
    navigateTo('catalogue');
  };
  const selectLessonSearchResult = (course, module) => navigateTo('lesson', course.id, module.id);

  const learnerNav = [
    {id:'clients',label:'Clients',icon:Building2},
    { id: 'portal',       label: 'My Learning',     icon: GraduationCap },
    { id: 'finder',       label: 'Find your course', icon: Search },
    ...(user.orgRole === 'manager' ? [{ id: 'organisation', label: 'My organisation', icon: Building2 }] : []),
    { id: 'catalogue',    label: 'Course Catalogue', icon: BookOpen },
    { id: 'library',      label: 'Library',          icon: LibraryIcon },
    { id: 'certificates', label: 'Certificates',     icon: Award },
    { id: 'profile',      label: 'My Profile',       icon: User },
  ];
  const courseEditItems = editableCourses.map(c => ({
    key: c.id,
    label: c.title,
    hint: c.edited ? 'edited' : undefined,
    active: view === 'course-edit' && editCourseId === c.id,
    onSelect: () => navigateTo('course-edit', null, null, c.id),
  }));
  const studioNav = canUseStudio && !isAdminView ? [{ id: 'presentation-studio', label: 'Presentation Studio', icon: Presentation }] : [];
  const facilitatorNav = [
    ...learnerNav,
    ...studioNav,
    { id: 'course-editing', label: 'Courses I edit', icon: PencilRuler, children: courseEditItems, searchable: courseEditItems.length > 8,
      searchPlaceholder: 'Search courses', emptyText: 'No courses assigned yet. Ask an Okiru admin to assign you courses.' },
  ];
  const userFilterItem = (key, label) => ({
    key, label, active: view === 'users' && usersFilter === key,
    onSelect: () => { setUsersFilter(key); navigateTo('users'); },
  });
  const adminNav = [
    {id:'clients',label:'Clients',icon:Building2},
    { id: 'admin',    label: 'Admin Dashboard', icon: LayoutGrid },
    { id: 'compliance', label: 'Compliance', icon: ClipboardCheck },
    { id: 'organisations', label: 'Organisations', icon: Building2 },
    { id: 'audit', label: 'Activity log', icon: History },
    { id: 'users-group', label: 'Users', icon: UsersIcon, children: [
      userFilterItem('all', 'All users'), userFilterItem('learner', 'Learners'),
      userFilterItem('facilitator', 'Facilitators'), userFilterItem('admin', 'Admins'),
    ] },
    { id: 'courses-group', label: 'Courses', icon: BookOpen, searchable: true, searchPlaceholder: 'Search courses to edit', children: [
      { key: '__catalogue', label: 'Browse the catalogue', pinned: true, active: view === 'catalogue', onSelect: () => navigateTo('catalogue') },
      ...courseEditItems,
    ] },
    { id: 'library',  label: 'Library',         icon: LibraryIcon },
    { id: 'presentation-studio', label: 'Presentation Studio', icon: Presentation },
    { id: 'feedback',  label: 'Feedback Inbox', icon: MessageSquare },
  ];

  return (
    <div className="app-shell">
      <a href="#main-content" className="skip-link">Skip to main content</a>
      <Sidebar
        user={user}
        navItems={isAdminView ? adminNav : isFacilitator ? facilitatorNav : [...learnerNav, ...studioNav]}
        activeView={view}
        setActiveView={(v) => navigateTo(v)}
        onLogout={() => handleLogout()}
        isAdminView={isAdminView}
        canSwitchView={user.role === 'admin'}
        onToggleViewMode={toggleViewMode}
      />
      <div className="app-main">
        <Header user={user} enrollments={enrollments} view={view} theme={theme} onToggleTheme={toggleTheme} onSearchCourse={selectCourseSearchResult} onSearchLesson={selectLessonSearchResult} />
        <main className={`app-content${view === 'lesson' ? ' app-content--flush' : ''}`} id="main-content" tabIndex={-1} ref={mainRef}>
          {pendingSaves > 0 && (
            <p className="sync-notice" role="status">
              {pendingSaves} lesson{pendingSaves === 1 ? '' : 's'} could not be saved yet. Your progress is kept on this device and will be saved when the connection returns.
            </p>
          )}
          <Suspense fallback={<LoadingSpinner />}>
          {view === 'clients' && <ClientWorkspace onLearning={()=>navigateTo(isAdminView?'admin':'portal')}/>}
          {view === 'portal'       && !isAdminView && <LearnerPortal {...ctx} />}
          {view === 'catalogue'    && <CourseCatalogue initialSearch={catalogueSearch} {...ctx} />}
          {view === 'finder'       && <GuidedFinder {...ctx} />}
          {view === 'library'      && <Library {...ctx} />}
          {view === 'presentation-studio' && canUseStudio && (
            <StudioShell user={user} canEdit={isAdminView} />
          )}
          {view === 'lesson'       && <LessonPlayer courseId={selectedCourse} moduleId={selectedModule} {...ctx} />}
          {view === 'certificates' && <Certificate {...ctx} />}
          {view === 'admin'        && isAdminView && (
            <AdminDashboard
              selectedCourseId={selectedCourse}
              editCourseId={editCourseId}
              onEditCourseConsumed={() => setEditCourseId(null)}
              {...ctx}
            />
          )}
          {view === 'profile'      && <Profile user={user} onProfileUpdate={(d) => setUser(u => ({ ...u, name: d.name, avatar_url: d.avatar_url }))} />}
          {view === 'feedback'     && isAdminView && <FeedbackInbox />}
          {view === 'compliance'   && isAdminView && <ComplianceView />}
          {view === 'audit'        && isAdminView && <AuditLogView />}
          {view === 'organisations' && isAdminView && <OrganisationsAdmin />}
          {view === 'organisation' && user.orgRole === 'manager' && <OrganisationView />}
          {view === 'users'        && isAdminView && (
            <UsersPanel user={user} filter={usersFilter} onFilterChange={setUsersFilter} onOpenCourse={(id) => navigateTo('course-edit', null, null, id)} />
          )}
          {view === 'course-edit'  && editCourseId && (isAdminView || isFacilitator) && (
            <CourseEditView courseId={editCourseId} user={user} onBack={() => { loadEditableCourses(); navigateTo(isAdminView ? 'admin' : 'portal'); }} />
          )}
          </Suspense>
        </main>
        <Footer />
      </div>
      {!isAdminView && (
        <FeedbackButton user={user} view={view} selectedCourse={selectedCourse} />
      )}
    </div>
  );
}
